import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  onSnapshot,
} from 'firebase/firestore';

const UserContext = createContext(null);

export function useUser() {
  return useContext(UserContext);
}

// eslint-disable-next-line arrow-body-style
const hasActiveSubscription = (subscriptions) => {
  return subscriptions?.some(
    (sub) =>
      sub.status === 'ACTIVE' &&
      (!sub.canceledDate || new Date(sub.canceledDate) > new Date())
  );
};

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [memberData, setMemberData] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);

  const auth = getAuth();
  const db = getFirestore();

  async function fetchMemberExtras() {
    if (!user) return;
    try {
      const memberRef = doc(db, 'members', user.squareCustomerId);
      const memberSnap = await getDoc(memberRef);
      if (memberSnap.exists()) {
        setMemberData(memberSnap.data());
        setSubscriptions(memberSnap.data().subscriptions || []);
      }
    } catch (error) {
      console.error("Error fetching member data:", error);
    }
  }

  useEffect(() => {
    fetchMemberExtras();
  }, [user]);

  useEffect(() => {
    if (!user || !user.uid) return;
  
    const memberRef = doc(db, 'members', user.squareCustomerId);
    const unsubscribe = onSnapshot(memberRef, (docSnap) => {
      if (docSnap.exists()) {
        setMemberData(docSnap.data());
      }
    });
  
    return () => unsubscribe();
  }, [user]);
  

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setUser(null);
        setMemberData(null);
        setSubscriptions([]);
        return;
      }

      const updatedUser = {
        uid: currentUser.uid,
        email: currentUser.email,
      };

      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userSnapshot = await getDoc(userDocRef);

        if (userSnapshot.exists()) {
          const userDataFromDb = userSnapshot.data();
          updatedUser.role = userDataFromDb.role || 'member';
          updatedUser.squareCustomerId = userDataFromDb.squareCustomerId || null; 
          updatedUser.activeMembership = userDataFromDb.activeMembership || false;
        } else {
          updatedUser.role = 'member';
          updatedUser.activeMembership = false;
          console.error("User doc not found in 'users' collection; defaulting role to 'member'.");
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
        updatedUser.role = 'member';
        updatedUser.squareCustomerId = null;
        updatedUser.activeMembership = false;
      }

      setUser(updatedUser);


      try {
        const q = query(
          collection(db, 'members'),
          where('email', '==', currentUser.email)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const memberDocRef = querySnapshot.docs[0].ref;
          const memberDocData = querySnapshot.docs[0].data();

          if (!memberDocData.uid) {
            await updateDoc(memberDocRef, { uid: currentUser.uid });
          }

          setMemberData({ ...memberDocData, id: memberDocRef.id, uid: currentUser.uid });
          const memberSubscriptions = memberDocData.subscriptions || [];
          setSubscriptions(memberDocData.subscriptions || []);
          const activeMembership = hasActiveSubscription(memberSubscriptions);

          try {
            await updateDoc(doc(db, 'users', currentUser.uid), {
              activeMembership
            });
            setUser((prev) => ({ ...prev, activeMembership }));
          } catch (updateError) {
            console.error('Error updating membership in database:', updateError);
          }
        } else {
          setMemberData(null);
          setSubscriptions([]);
        }
      } catch (error) {
        console.error('Error fetching member data:', error);
        setMemberData(null);
        setSubscriptions([]);
      }
    });

    return () => unsubscribe();
  }, [auth, db]);


  async function switchMember(newMemberId) {
    try {
      const childRef = doc(db, 'members', newMemberId);
      const snap = await getDoc(childRef);
      if (snap.exists()) {

        setMemberData({ id: snap.id, ...snap.data() });
      } else {
        console.warn(`No member doc`);
      }
    } catch (err) {
      console.error('Error switching member:', err);
    }
  }

  const value = useMemo(
    () => ({
      subscriptions,
      user,
      memberData,
      switchMember,
      fetchMemberExtras,
    }),
    [user, memberData, subscriptions]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
